import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import type { ActiveRunDto, HubPlanetDto } from '@aiai/shared';
import { formatDuration } from '../lib/format';
import { agentTagForRun } from './layout';
import { planetHref } from './needsYou';
import { patchNames } from './patches';

function host(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/** Only a live deploy is green and only a failed one red; stopped/building/… stay neutral. */
function liveTone(status: string): string {
  if (status === 'live') return 'text-ok';
  if (status === 'failed') return 'text-bad';
  return 'text-ink-muted';
}

export function PlanetPeekCard({
  planet, runs, showWorkspace, onClose,
}: { planet: HubPlanetDto; runs: ActiveRunDto[]; showWorkspace: boolean; onClose: () => void }) {
  const label = planet.code ?? planet.name;
  const patches = patchNames(planet);
  // Escape closes the card wherever focus is (it is not modal, so focus may be on the planet).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div role="dialog" aria-label={`${label} details`} className="card w-full p-3 shadow-sm sm:w-72">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-display text-2xs font-bold uppercase tracking-[0.08em] text-ink-muted">
            {planet.code ? `${planet.code} · ${planet.name}` : planet.name}
          </div>
          {showWorkspace && <div className="text-2xs text-ink-faint">{planet.workspace.name}</div>}
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className="text-ink-faint hover:text-ink">
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
      <ul className="mt-2 space-y-1.5">
        {runs.length === 0 ? (
          <li className="text-xs text-ink-muted">{planet.autonomy === 'manual' ? 'Paused' : 'Idle'}</li>
        ) : (
          runs.map((r) => (
            <li key={r.id} className="text-xs">
              <span className="font-semibold text-ink">{agentTagForRun(r) === 'E' ? 'Engineer' : 'PM'}</span>
              {r.issueCode && <span className="ml-1.5 font-mono text-ink-faint">{r.issueCode}</span>}
              {r.loopCount > 0 && <span className="ml-1.5 text-ink-muted">retry {r.loopCount}</span>}
              <span className="ml-1.5 tabular-nums text-ink-muted">{formatDuration(r.startedAt ?? r.createdAt, null)}</span>
              <span className="block truncate font-mono text-2xs text-ink-muted" title={r.lastLogLine ?? undefined}>
                {r.lastLogLine ?? 'starting…'}
              </span>
            </li>
          ))
        )}
      </ul>
      <div className="mt-2 font-mono text-2xs text-ink-muted">
        {planet.openIssueCount} open · {planet.shippedCount} shipped
      </div>
      {patches.length > 0 && (
        <div className="font-mono text-2xs text-ink-muted">Patches: {patches.join(' \u00B7 ')}</div>
      )}
      {planet.live && (
        <div className={`font-mono text-2xs ${liveTone(planet.live.status)}`}>
          {planet.live.status} · {host(planet.live.url)}
        </div>
      )}
      <Link to={planetHref(planet)} className="mt-2 inline-block text-xs font-semibold text-ink hover:underline">
        Open {planet.kind === 'prototype' ? 'prototype' : 'project'} →
      </Link>
    </div>
  );
}
